import store from "@invesco/shared/store/index";
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils.js";
import analytics from "@react-native-firebase/analytics";
import * as Application from "expo-application";
import Constants from "expo-constants";
import { Platform } from "react-native";

const isNative = Platform.OS === "android" || Platform.OS === "ios";
const FIREBASE_MAX_ARRAY_ITEMS = 100;
const FIREBASE_ITEMS_EVENTS = new Set([
  "view_item",
  "view_item_list",
  "select_item",
  "add_to_cart",
  "begin_checkout",
  "add_payment_info",
  "purchase",
  "refund",
]);

type FirebaseScalarParam = string | number;

// Event name is now a string to allow maximum flexibility as per team lead's feedback

interface EventParams {
  click_text?: string;
  click_category?: string;
  click_header?: string;
  custom_current_page?: string;
  custom_previous_page?: string;
  user_status?: string;
  userID?: string;
  custom_index?: number | string;
  search_term?: string;
  no_of_results?: number;
  no_of_funds?: number | string;
  investment_period?: string;
  categories_selected?: string;
  plan_option_selected?: string;
  investment_type?: string;
  investment_option?: string;
  investment_plan?: string;
  folio?: string;
  custom_nav?: string | number;
  custom_cagr?: string | number;
  item_name?: string;
  frequency?: string;
  affiliation?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_category2?: string;
  [key: string]: any;
}

function getCommonParams() {
  const state = (store?.getState?.() as any) || ({} as any);
  const pan = state?.user?.pan || "-";
  const token = state?.user?.token;
  const isLoggedIn = state?.user?.isLoggedIn;
  const user_status = isLoggedIn ? "Logged In" : "Guest";

  // Hash PAN to create userID for privacy (hex-encoded SHA-256, truncated to 25 chars
  // for system compatibility, same hashing as before).
  const userID = pan !== "-" ? bytesToHex(sha256(utf8ToBytes(pan))).slice(0, 25) : "-";

  const app_version =
    Constants.expoConfig?.version ?? Application.nativeApplicationVersion ?? "0.0.0";
  const platform = Platform.OS;

  return { userID, user_status, app_version, platform };
}

function normalizeFirebaseScalar(value: any): FirebaseScalarParam | undefined {
  if (value === undefined || value === null) return undefined;

  if (typeof value === "string" || typeof value === "number") return value;

  if (typeof value === "boolean") return String(value);

  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeFirebaseScalar(item))
      .filter((item) => item !== undefined)
      .join(", ");
  }

  if (value instanceof Date) return value.toISOString();

  if (typeof value === "object") {
    const normalizedObject = Object.entries(value).reduce<
      Record<string, FirebaseScalarParam>
    >((acc, [key, itemValue]) => {
      const normalizedValue = normalizeFirebaseScalar(itemValue);
      if (normalizedValue !== undefined) {
        acc[key] = normalizedValue;
      }
      return acc;
    }, {});

    return JSON.stringify(normalizedObject);
  }

  return String(value);
}

function normalizeFirebaseItem(item: any) {
  if (!item || typeof item !== "object" || Array.isArray(item)) {
    return { item_name: normalizeFirebaseScalar(item) ?? "-" };
  }

  return Object.entries(item).reduce<Record<string, FirebaseScalarParam>>(
    (acc, [key, value]) => {
      const normalizedValue = normalizeFirebaseScalar(value);
      if (normalizedValue !== undefined) {
        acc[key] = normalizedValue;
      }
      return acc;
    },
    {},
  );
}

function normalizeFirebaseEventParams(eventName: string, params: Record<string, any>) {
  const { ecommerce, ...rest } = params;
  const normalized: Record<string, any> = { ...rest };

  if (
    FIREBASE_ITEMS_EVENTS.has(eventName) &&
    ecommerce &&
    typeof ecommerce === "object" &&
    !Array.isArray(ecommerce)
  ) {
    Object.assign(normalized, ecommerce);
  }

  delete normalized.ecommerce;
  delete normalized.pan;

  Object.keys(normalized).forEach((key) => {
    const value = normalized[key];

    if (Array.isArray(value) && key === "items") {
      if (FIREBASE_ITEMS_EVENTS.has(eventName)) {
        normalized[key] = value
          .slice(0, FIREBASE_MAX_ARRAY_ITEMS)
          .map(normalizeFirebaseItem);
      } else {
        delete normalized[key];
      }
      return;
    }

    const normalizedValue = normalizeFirebaseScalar(value);
    if (normalizedValue === undefined) {
      delete normalized[key];
    } else {
      normalized[key] = normalizedValue;
    }
  });

  return normalized;
}

export const Analytics = {
  /**
   * Identifies the user in Firebase.
   * Should be called after successful login or when app state updates with user info.
   */
  async identifyUser(userId: string) {
    if (!isNative || !userId || userId === "-" || userId === "unknown") return;
    try {
      await analytics().setUserId(String(userId));
      await analytics().setUserProperties({ pan: userId });
      // console.log("[Analytics] User ID & Properties set:", userId);
    } catch (error) {
      console.error("[Analytics] Error identifying user:", error);
    }
  },

  /**
   * Generic event logger that merges common parameters with event-specific data.
   */
  async logEvent(name: string, params?: EventParams) {
    if (!isNative) return;
    try {
      const common = getCommonParams();

      // Ensure user is identified if userID is available
      if (common.userID && common.userID !== "-") {
        await Analytics.identifyUser(common.userID);
      }

      const eventData = {
        ...common,
        ...params,
      };

      const finalParams = normalizeFirebaseEventParams(name, eventData);

      await analytics().logEvent(name, finalParams);
      // console.log(`[Analytics] [Event] ${name}:`, JSON.stringify(finalParams, null, 2));
    } catch (error) {
      console.error(`[Analytics] Error logging event ${name}:`, error);
    }
  },

  async logScreenView(screenName: string, screenClass?: string) {
    if (!isNative) return;
    try {
      const common = getCommonParams();
      if (common.userID && common.userID !== "-") {
        await Analytics.identifyUser(common.userID);
      }

      const params = {
        screen_class: screenClass || screenName,
        ...common,
      };

      await analytics().logScreenView({
        screen_name: screenName,
        screen_class: screenClass || screenName,
      });
      // console.log(`[Analytics] [Screen] ${screenName}:`, params);
    } catch (error) {
      console.error("[Analytics] Error logging screen view:", error);
    }
  },

  async logPurchase(params: {
    transactionId: string;
    value: number;
    currency?: string;
    payment_type?: string;
    custom_current_page?: string;
    custom_previous_page?: string;
    investment_type?: string;
    folio?: string;
    frequency?: string;
    items: Array<{
      item_id: string;
      item_name: string;
      affiliation?: string;
      index?: number;
      item_brand?: string;
      investment_option?: string;
      investment_plan?: string;
      item_category?: string;
      item_category2?: string;
      item_list_id?: string;
      item_list_name?: string;
      price: number;
      quantity?: number;
    }>;
  }) {
    if (!isNative) return;
    try {
      const common = getCommonParams();
      if (common.userID && common.userID !== "-") {
        await Analytics.identifyUser(common.userID);
      }

      const purchaseParams = {
        transaction_id: params.transactionId,
        value: params.value,
        currency: params.currency || "INR",
        customer_type: "new",
        payment_type: params.payment_type,
        custom_current_page: params.custom_current_page || "thankyou page",
        custom_previous_page: params.custom_previous_page || "add payment info",
        investment_type: params.investment_type,
        folio: params.folio,
        frequency: params.frequency,
        items: params.items,
        ...common,
      };

      // Globally remove PAN from parameters to ensure privacy
      delete (purchaseParams as any).pan;

      const finalParams = normalizeFirebaseEventParams("purchase", purchaseParams);

      await analytics().logEvent("purchase", finalParams);
      // console.log("[Analytics] [Event] purchase:", JSON.stringify(finalParams, null, 2));
    } catch (error) {
      console.error("[Analytics] Error logging purchase:", error);
    }
  },

  async logSwitchSuccess(params: {
    click_text: string;
    click_category: string;
    fund_out_from: string;
    fund_in_to: string;
    custom_amount: number | string;
    custom_current_page?: string;
    custom_previous_page?: string;
    folio?: string;
  }) {
    return Analytics.logEvent("switch_successful", {
      ...params,
      custom_current_page: params.custom_current_page || "thankyou page",
      custom_previous_page: params.custom_previous_page || "funds switch",
    });
  },

  async logStpSuccess(params: {
    click_text: string;
    click_category: string;
    fund_out_from: string;
    fund_in_to: string;
    custom_amount: number | string;
    folio?: string;
    investment_plan?: string;
    investment_option?: string;
    stp_type?: string;
    frequency?: string;
    custom_current_page?: string;
    custom_previous_page?: string;
  }) {
    return Analytics.logEvent("stp_successful", {
      ...params,
      custom_current_page: params.custom_current_page || "thankyou page",
      custom_previous_page: params.custom_previous_page || "Start STP",
    });
  },

  async logSwpSuccess(params: {
    click_text: string;
    click_category: string;
    custom_amount: number | string;
    folio?: string;
    investment_plan?: string;
    investment_option?: string;
    swp_type?: string;
    frequency?: string;
    custom_current_page?: string;
    custom_previous_page?: string;
    bank_name?: string;
    items?: any[];
    fund_name?: string;
    amount?: number | string;
    investment_type?: string;
  }) {
    const isRedeem = params.click_category?.toLowerCase() === "redeem";
    const eventName = isRedeem ? "redeem_success" : "swp_successful";
    const extraFields: Record<string, any> = params.items
      ? {
          currency: "INR",
          transaction_id: params.folio || "T_N/A",
          bank_name: params.bank_name,
          items: params.items,
        }
      : {};
    return Analytics.logEvent(eventName, {
      ...params,
      custom_current_page: params.custom_current_page || "thankyou page",
      ...extraFields,
    });
  },
};
